import api from "./api";
import {
  Routes,
  Route,
  Link as RouterLink,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Cookies from "js-cookie";
import CryptoJS from "crypto-js";
import { useState, useEffect, useContext, createContext } from "react";
import { ThemeProvider } from "@mui/material/styles";
import LoginForm from "./components/login";
import FirstConnexion from "./components/firstConnexion";
import ForgotPassword from "./components/passwordReinitialization";
import PasswordUpdate from "./components/passwordUpdate";
import SalonList from "./components/salonsList";
import FicheSalon from "./components/ficheSalon";
import UpdateFicheSalon from "./components/updateFicheSalons";
import { SalonDataProvider } from './components/salonDataContext';
import UpdateUser from "./components/userUpdate";
import UserContext from "./components/userContext";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import AccountInit from "./components/accountInit";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import CssBaseline from "@mui/material/CssBaseline";
import ionis_logo from "./images/ionis_logo.png";
import baseTheme from "./theme";

export const DeadlineContext = createContext();

function LogoutButton() {
  const navigate = useNavigate();
  
  const { setIsLoggedIn, setRole } = useContext(UserContext);

  const handleLogout = () => {
    setIsLoggedIn(false);
    setRole(null);
    Cookies.remove("user");
    localStorage.removeItem('jwtToken');
    navigate("/login");
  };

  return (
    <Button
      variant="contained"
      color="error"
      onClick={handleLogout}
      sx={{ ml: 1, whiteSpace: "nowrap" }}
    >
      Déconnexion
    </Button>
  );
}

function App() {
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [role, setRole] = useState(null);
  const [user, setUser] = useState(null);
  const [deadline, setDeadline] = useState(null);
  const [firstName, setFirstName] = useState(null);
  const location = useLocation();

  const isUpdatingSalon = location.pathname ? location.pathname.includes("/UpdateFicheSalon") : false;
  const showAppBar =
    isLoggedIn && location.pathname !== "/" && location.pathname !== "/login";

  const fetchDeadline = async () => {
    try {
      const response = await api.get("/events/getDeadline");
      if (response.data && response.data.data[0].Deadline__c) {
        setDeadline(new Date(response.data.data[0].Deadline__c));
      } else {
        console.error("Deadline data is missing in the response");
      }
    } catch (error) {
      console.error("Error fetching deadline:", error);
    }
  };

  useEffect(() => {
    fetchDeadline();
  }, []);

  // Optimiser - vérifier la connexion une seule fois au démarrage
  useEffect(() => {
    // Try to restore session from cookie first
    const userCookie = Cookies.get('user');
    if (userCookie) {
      api.get("/events/key", { withCredentials: true })
        .then((response) => {
          const key = response.data.key;
          try {
            const bytes = CryptoJS.AES.decrypt(userCookie, key);
            const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
            setUser(decryptedData);
            setRole(decryptedData.Role__c || decryptedData.role);
            const restoredFirstName = decryptedData.FirstName__c || decryptedData.firstName;
            setFirstName(restoredFirstName);
            setIsLoggedIn(true);
            // Force a re-render if firstName is present but not reflected in UI
            if (restoredFirstName) {
              setTimeout(() => setFirstName(restoredFirstName), 50);
            }

          } catch (e) {
            setIsLoggedIn(false);
            setUser(null);
            setRole(null);
            setFirstName(null);
            Cookies.remove('user');
            localStorage.removeItem('jwtToken');
          }
        })
        .catch(() => {
          setIsLoggedIn(false);
          setUser(null);
          setRole(null);
          setFirstName(null);
          Cookies.remove('user');
          localStorage.removeItem('jwtToken');
        })
        .finally(() => setLoading(false));
      return;
    }
    // Fallback to JWT in localStorage if no cookie
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      setLoading(false);
      setIsLoggedIn(false);
      return;
    }
    const checkLoginAndLoadData = async () => {
      try {
        const loginResponse = await api.get("/events/isLoggedIn", {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        if (loginResponse.data.isLoggedIn) {
          setIsLoggedIn(true);
          const userResponse = await api.get("/events/getLogin", {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true,
          });
          const userData = userResponse.data.data[0];
          setUser(userData);
          setRole(userData.Role__c);
          setFirstName(userData.FirstName__c);
        } else {
          setIsLoggedIn(false);
          localStorage.removeItem('jwtToken');
        }
      } catch (error) {
        setIsLoggedIn(false);
        localStorage.removeItem('jwtToken');
      } finally {
        setLoading(false);
      }
    };
    checkLoginAndLoadData();
  }, []);

  // Suppression du code qui efface le token JWT/Cookie à la fermeture ou au refresh

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <UserContext.Provider value={{ 
      user, 
      setUser, 
      setIsLoggedIn, 
      isLoggedIn, 
      role, 
      setRole,
      firstName,
      setFirstName  // Ajouter cette ligne
    }}>
      <DeadlineContext.Provider value={deadline}>
        <SalonDataProvider>
          <ThemeProvider theme={baseTheme}>
            <CssBaseline />
            {showAppBar && (
              <AppBar position="fixed" elevation={1}>
                <Toolbar sx={{ gap: 1 }}>
                  <Box
                    component="img"
                    src={ionis_logo}
                    alt="IONIS"
                    sx={{ height: { xs: 28, md: 36 }, mr: 1 }}
                  />
                  <Typography
                    variant="h6"
                    noWrap
                    sx={{ flexGrow: 1, fontSize: { xs: "0.9rem", md: "1.125rem" } }}
                  >
                    Vos salons de l'année et leurs bilans
                  </Typography>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ display: { xs: "none", md: "block" }, opacity: 0.9 }}
                  >
                    {firstName ? `Bienvenue ${firstName} !` : "Bienvenue !"}
                  </Typography>
                  {!isUpdatingSalon && (
                    <>
                      <Button
                        color="inherit"
                        component={RouterLink}
                        to="/SalonsList"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Vos salons
                      </Button>
                      <LogoutButton />
                    </>
                  )}
                </Toolbar>
              </AppBar>
            )}
            {/* Espaceur : compense la hauteur de l'AppBar fixe. */}
            {showAppBar && <Toolbar />}
            <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
              <Routes>
                <Route path="/" element={<LoginForm setRole={setRole} />} />
                <Route path="/login" element={isLoggedIn ? <SalonList /> : <LoginForm />} />
                <Route path="/ForgotPassword" element={<ForgotPassword />} />
                <Route path="/First-login" element={<FirstConnexion />} />
                <Route path="/PasswordUpdate/:token" element={<PasswordUpdate />} />
                <Route path="/SalonsList" element={!isLoggedIn ? <LoginForm /> : <SalonList />} />
                <Route path="/FicheSalon/:id" element={!isLoggedIn ? <LoginForm /> : <FicheSalon />} />
                <Route
                  path="/UpdateFicheSalon/:id"
                  element={!isLoggedIn ? <LoginForm /> : <UpdateFicheSalon />}
                />
                <Route path="/UpdateUser/:id" element={isLoggedIn ? (role === "Admin" ? <UpdateUser setRole={setRole} /> : <SalonList />) : <LoginForm />} />
                <Route path="/accountInit/:token" element={<AccountInit />} />
              </Routes>
            </Box>
          </ThemeProvider>
        </SalonDataProvider>
      </DeadlineContext.Provider>
    </UserContext.Provider>
  );
}

export default App;










